/* The toast host and its `useToast` hook ship together — the hook is the only
 * way to reach the host's context. */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cx } from '../lib/cx';

export type ToastTone = 'default' | 'error';

export interface ToastOptions {
  tone?: ToastTone;
  /** Auto-dismiss delay in ms. Default 2400 (design-reference §6.9); 0 disables. */
  duration?: number;
}

interface ToastEntry {
  id: number;
  message: string;
  tone: ToastTone;
  duration: number;
}

interface ToastApi {
  toast: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Access the toast API. Throws if used outside `<ToastProvider>`. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Single-slot (mock behaviour) vs a stack. Default single. */
  stack?: boolean;
}

/**
 * Hosts the toast slot. Each toast is announced via `role="status"`
 * (`aria-live="polite"`) or `role="alert"` for errors (a11y §8.6.24), carries a
 * dismiss control, and auto-dismisses after a configurable duration.
 */
export function ToastProvider({ children, stack = false }: ToastProviderProps): React.JSX.Element {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, options: ToastOptions = {}): number => {
      const id = nextId.current++;
      const entry: ToastEntry = {
        id,
        message,
        tone: options.tone ?? 'default',
        duration: options.duration ?? 2400,
      };
      setToasts((current) => (stack ? [...current, entry] : [entry]));
      return id;
    },
    [stack],
  );

  const api = useMemo<ToastApi>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ds-toast-host">
        {toasts.map((entry) => (
          <ToastItem key={entry.id} entry={entry} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ entry, onDismiss }: { entry: ToastEntry; onDismiss: (id: number) => void }): React.JSX.Element {
  useEffect(() => {
    if (entry.duration <= 0) return;
    const timer = setTimeout(() => {
      onDismiss(entry.id);
    }, entry.duration);
    return () => {
      clearTimeout(timer);
    };
  }, [entry, onDismiss]);

  return (
    <div
      className={cx('ds-toast', entry.tone === 'error' && 'ds-toast--error')}
      role={entry.tone === 'error' ? 'alert' : 'status'}
      aria-live={entry.tone === 'error' ? 'assertive' : 'polite'}
    >
      <span>{entry.message}</span>
      <button
        type="button"
        className="ds-toast__dismiss"
        aria-label="Dismiss notification"
        onClick={() => {
          onDismiss(entry.id);
        }}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
}
